import { apiUrl } from "@/lib/api";

// Shared post-authentication helpers used by both LoginPage and the landing
// social quick-signup. Keeping these in one place means email login, social
// login, and social signup all route users identically.

// Store JWT (access/refresh) or DRF-token style responses. Throws if the
// response carried no usable credential.
export function storeAuthTokens(data) {
  // A fresh sign-in is a fresh person as far as this browser knows: an invite
  // arrival left by somebody else must not ask them for a password. The invite
  // page records its own arrival AFTER storing the token.
  try { localStorage.removeItem("sift_invite_arrival"); } catch { /* private mode */ }
  if (data?.access && data?.refresh) {
    localStorage.setItem("access", data.access);
    localStorage.setItem("refresh", data.refresh);
  } else if (data?.token || data?.auth_token || data?.key) {
    const t = data.token || data.auth_token || data.key;
    localStorage.setItem("authToken", t);
  } else {
    throw new Error("Login response did not include a token.");
  }
}

// Decide where a freshly authenticated user should land. Mirrors the cream
// label / company / contractor / time-entry routing rules. Safe on error
// (falls back to a sensible default). Must run AFTER storeAuthTokens.
const TIME_ENTRY_ROLES = new Set(["Time Entry - Clock", "Time Entry - Manual"]);
// People who work for a company rather than run it. They land on their own
// dashboard (timecards, contracts, documents, pay, travel), never the hub,
// unless they also own a company, because the company switcher is on the hub.
export const WORKER_ROLES = new Set(["Member", "Authorized User"]);

// A `next=` worth following: a path on this site, or null. "/" alone was the
// test, and "//evil.com" or "/\evil.com" pass it while meaning another site
// (react-router GHSA-wrjc-x8rr-h8h6 sends the backslash form off-site).
export function safeNextPath(value) {
  const next = (value || "").trim();
  // A backslash, or a tab or newline the browser would strip, can turn the
  // path into "//host".
  const sneaky = [...next].some((ch) => ch === "\\" || ch.charCodeAt(0) < 32);
  if (!next.startsWith("/") || next.startsWith("//") || sneaky) {
    return null;
  }
  return next;
}

// `knownUser` is the user object the auth response already handed us. When it
// is present we skip the /me/ round trip below, which was the last thing
// standing between a successful login and the redirect: a whole request across
// the Atlantic to read one role string the server had already told us. Optional
// on purpose — every other caller passes nothing and keeps the old behaviour,
// so this could not regress the social and signup flows that share this helper.
export async function resolvePostLoginPath(branding, knownUser = null) {
  const accessToken = localStorage.getItem("access");
  const authToken = localStorage.getItem("authToken");
  const token = accessToken || authToken;
  const headers = { Accept: "application/json", "Content-Type": "application/json" };
  if (token) headers.Authorization = accessToken ? `Bearer ${token}` : `Token ${token}`;

  // Time-entry-only roles ALWAYS go to /clock, regardless of cream label
  // or contractor/company shape.
  //
  // Test for the KEY, not for a truthy value. Most people have no org role at
  // all, so the server legitimately answers `role: null` — treating that as
  // "unknown" would send every ordinary user back to /me/ and buy nothing.
  // A payload from a backend older than this field has no key, so it still
  // falls through to the fetch, which is what makes the two sides safe to
  // deploy in either order.
  const serverKnowsRole =
    !!knownUser &&
    ("role" in knownUser || (knownUser.organization && "role" in knownUser.organization));

  if (serverKnowsRole) {
    const knownRole = knownUser.organization?.role ?? knownUser.role;
    if (TIME_ENTRY_ROLES.has(knownRole)) return "/clock";
    if (WORKER_ROLES.has(knownRole) && !knownUser.is_org_owner) return "/dashboard/contractor";
  } else {
    try {
      const meRes = await fetch(apiUrl("/api/accounts/me/"), { headers });
      if (meRes.ok) {
        const me = await meRes.json();
        if (TIME_ENTRY_ROLES.has(me?.organization?.role)) {
          return "/clock";
        }
        if (WORKER_ROLES.has(me?.organization?.role) && !me?.is_org_owner) {
          return "/dashboard/contractor";
        }
      }
    } catch {}
  }

  if (!branding?.branded) {
    // Everyone lands on the hub. This used to fork — company owners were sent
    // straight to /company/dashboard and everyone else to /home — which meant
    // two people on the same product had no shared starting point, and someone
    // who is both an owner AND doing contract work could only reach one of
    // their two sides by knowing a URL. /home shows both and remembers nothing,
    // so there is one answer to "where am I after signing in".
    return "/home";
  }

  // On cream label portals, auto-activate the cream label org and route to
  // the company dashboard.
  try {
    const orgId = branding.organization_id;
    if (orgId) {
      const activateRes = await fetch(apiUrl("/api/accounts/me/organizations/activate/"), {
        method: "POST",
        headers,
        body: JSON.stringify({ organization_id: orgId }),
      }).catch(() => null);
      if (activateRes?.ok) return "/company/dashboard";
    }

    let isOrgSide = false;
    const meRes = await fetch(apiUrl("/api/accounts/me/"), { headers });
    if (meRes.ok) {
      const meBody = await meRes.json();
      if (meBody.organization?.id) return "/company/dashboard";
      // A company that signed up with one tap has no organisation yet: OAuth
      // hands us a person, never a company name. Remember it, because the
      // fallback at the bottom of this function is the contractor dashboard
      // and that is exactly the wrong room to put them in.
      isOrgSide = !!meBody.is_org_owner;
    }

    const orgsRes = await fetch(apiUrl("/api/accounts/me/organizations/"), { headers }).catch(() => null);
    if (orgsRes?.ok) {
      const orgsData = await orgsRes.json();
      const orgs = Array.isArray(orgsData?.organizations) ? orgsData.organizations : [];
      if (orgs.length > 0) {
        const activateRes = await fetch(apiUrl("/api/accounts/me/organizations/activate/"), {
          method: "POST",
          headers,
          body: JSON.stringify({ organization_id: orgs[0].id }),
        }).catch(() => null);
        if (activateRes?.ok) return "/company/dashboard";
      }
    }

    /* Org owner, no organisation: ask for the company rather than dropping
       them on the wrong dashboard. This is the last step of a company signup,
       not an error state.

       /auth/organization/register, not /company/onboarding. Onboarding is the
       settings screen for a company that already exists -- with no org it
       renders an empty shell and a small "Create / Join" link, which is a dead
       end dressed as a destination. The register screen, for somebody already
       signed in, asks for the company and POSTs it to
       /api/accounts/organizations/, which creates the org, makes them Owner,
       and runs the founding-place assignment. Then it goes to onboarding
       itself. */
    if (isOrgSide) return "/auth/organization/register";
    return "/dashboard/contractor";
  } catch {}
  return "/dashboard/contractor"; // default to contractor on cream label
}
